/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {navigateReport} from '@ion/applications/src/pages/analytics/store';
import Device from '@ion/core/src/services/Device';
import {DashboardId} from '@ion/core/src/services/Sisense';
import {
  CVSAssetType,
  DevicePeripheralTypes,
  DeviceSpreaderSearchTypes,
  GenericSensorAttachmentTypes
} from '@ion/core/src/types/entities';
import {DeviceFormFields} from '@ion/dls/src/editors/DeviceEditorUtils';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {AssetLabelTypes, DLSTheme} from '@ion/dls/src/types/types';
import {augmentSensorData} from '@ion/features/src/smarterTrailer/SmarterTrailerUtils';
import {useGenericSensor} from '@ion/query/src/hooks/useGenericSensor';
import {useLoadSmartTrailer} from '@ion/query/src/hooks/useLoadSmartTrailer';
import {useSpreaderData} from '@ion/query/src/hooks/useSpreaderData';
import useAssociatedDevice from '@/routes/tracking2/views/hooks/useAssociatedDevice';
import Features, {hasAnalyticsPageAccess} from '@/security/Features';
import {setSisenseDefaultDashboard} from '../../store/actions/reports';
import {
  setFindNearbyAsset,
  setFindNearbyPopup
} from '../../store/actions/tracking';
import {
  desktopSideSheetOpenSelector,
  mobileSideSheetOpenSelector
} from '../../store/selectors/nav-selectors';
import {
  selectedAssetSelector,
  sensorUpdateLoadingSelector
} from '../../store/selectors/tracking-selectors';
import changePage, {Route} from '../RouteDefinitions';
import {useIsFilterSelected} from './hooks/sidesheet/useIsFilterSelected';
import GenericSensorAccordion from './sc/genericsensor/GenericSensorAccordion';
import SCDeviceSensorGraphs from './sc/reports/SCDeviceSensorGraphs';
import {SpreaderMessageAccordion} from './sidesheetdetails/SpreaderMessageAccordion';
import {VisionEventsAccordion} from './sidesheetdetails/VisionEventsAccordion';
import AdditionalInfoAccordion from './AdditionalInfoAccordion';
import AssetDetailsAccordion from './AssetDetailsAccordion';
import AssetDetailsDiagnostics from './AssetDetailsDiagnostics';
import {SmarterTrailerAccordion} from './SmarterTrailerAccordion';
import {TagsAccordion} from './TagsAccordion';

export type AssetDetailOverviewPanelProps = {
  asset: Device;
  labels: AssetLabelTypes;
  assetDetailView?: boolean;
  onContainsClick?: (asset: CVSAssetType) => void;
  toolPanelOpen?: {
    assetId?: string | null;
    open?: boolean;
  }[];
  onSelectAssociatedAsset: Function;
  parentHeight?: number;
  trackingV2?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  assetDetailOverviewWrapper : {
    position                       : 'absolute',
    overflowY                      : 'auto',
    overflowX                      : 'hidden',
    top                            : (topValue) => `${topValue}px`,
    bottom                         : '0',
    left                           : '0',
    right                          : '0',
    [theme.breakpoints.down('sm')] : {
      position : 'relative',
      height   : 'calc(100vh - 230px) !important',
      top      : '0 !important'
    }
  },
  assetCardListContentWrapper : {
    padding : '0px 16px'
  }
}));

/**
 * Defines the content of the Asset Detail Overview tab. This component is
 * intended to be used within the context of the Asset Detail sidesheet
 * display on the iOn Suite tracking page.
 *
 * ```typescript
 * import AssetDetailOverviewPanel from './AssetDetailOverviewPanel';
 *
 * <AssetDetailOverviewPanel ... />
 *
 * ```
 */
const AssetDetailOverviewPanel = (props: AssetDetailOverviewPanelProps) => {
  const containerRef           = useRef(null);
  const isPhone                = useIsPhone();
  const dispatch               = useDispatch();
  const asset                  = useSelector(selectedAssetSelector);
  const device                 = asset?.getDevice();
  const sensorUpdateLoading    = useSelector(sensorUpdateLoadingSelector);
  const isDesktopSideSheetOpen = useSelector(desktopSideSheetOpenSelector);
  const isMobileSideSheetOpen  = useSelector(mobileSideSheetOpenSelector);
  const isFilterSelected       = useIsFilterSelected();
  const isScDevice             = asset?.isScDevice?.();

  const topValue = useMemo(() => {
    if (isCurrentTheme()) {
      return 168;
    }
    const {parentHeight} = props;
    return parentHeight && parentHeight < 100 ? parentHeight + 52 : 112;
  }, [props.parentHeight]);

  const classes = useStyles(topValue);

  const {
    result: selectedAssetTrailer,
    isLoading: smartTrailerLoading
  } = useLoadSmartTrailer({
    device : asset,
    augmentSensorData,
    Features
  });

  const spreaderDevice = useAssociatedDevice(device, DeviceFormFields.ATTACHMENTS);
  
  const attachedPeripheralType = useMemo(() =>
    spreaderDevice?.features?.[DeviceFormFields.ATTACHMENTS] || null, [spreaderDevice]);

  const isGenericSensorSelected = useMemo(() => {
    return attachedPeripheralType?.includes(GenericSensorAttachmentTypes.GENERICSENSOR);
  }, [attachedPeripheralType]);

  const isSpreaderSelected = useMemo(() => {
    const {CIRRUS, FORCEAMERICA, DICKEYJOHN} = DevicePeripheralTypes || {};
    return [CIRRUS, FORCEAMERICA, DICKEYJOHN]?.some(type => attachedPeripheralType?.includes(type));
  }, [attachedPeripheralType]);

  const sensorApps = useMemo(() => (
    attachedPeripheralType?.split(',')?.map?.(type => DeviceSpreaderSearchTypes[type]).filter(Boolean)
  ), [attachedPeripheralType]);

  const {
    genericSensorData,
    isLoading: genericSensorLoading
  } = useGenericSensor({
    deviceEsn : device?.deviceEsn,
    enabled   : isGenericSensorSelected && !isNil(device?.deviceEsn)
  });

  const spreaderDeviceESN = spreaderDevice?.esn;

  const {
    data,
    spreaders,
    isLoading: spreaderIsLoading
  } = useSpreaderData({
    id : spreaderDeviceESN,
    sensorApps
  },
  !isNil(spreaderDeviceESN) && isSpreaderSelected, 10);

  const isSideSheetOpen        = isDesktopSideSheetOpen || isMobileSideSheetOpen;
  const diagnosticDataExists   = !asset?.isScDevice() && verifyDiagnosticsData(asset?.getDevice() as any);
  const assetHasSmarterTrailer = asset?.isCVSAsset() && !!selectedAssetTrailer;
  const hasIOnVision           = Features.hasIOnVision();

  const findNearest = useCallback(function() {
    dispatch(setFindNearbyAsset(asset));
    dispatch(setFindNearbyPopup(true));
  }, [asset]);

  function verifyDiagnosticsData(props: AssetDetailOverviewPanelProps) {
    const diagnosticFields = {
      ignition                : true,
      vbOdometer              : true,
      gpsOdometer             : true,
      vbFuelPercent           : true,
      adcDeviceBatteryVoltage : true,
      adcExternalPowerVoltage : true,
      dtcCodes                : true
    };
    const fields = Object.keys(props)?.filter(field => diagnosticFields[field]);
    const values = fields ? fields.filter(key => props[key] !== '') : [];
    return values.length > 0;
  }

  const onViewTireSensorReport = useCallback(() => {
    dispatch(setSisenseDefaultDashboard(DashboardId.TireSensor));
    navigateReport({
      oid : DashboardId.TireSensor
    });
    changePage(Route.ANALYTICS_DASHBOARDS);
  }, []);

  const onTouchHandler = useCallback((event) => {
    const ref = containerRef.current;
    const scrollTop = ref?.scrollTop;
    if (isPhone && !Number.isNaN(scrollTop) && scrollTop !== 0) {
      event.stopPropagation();
    }
  }, [isPhone]);

  const assetDetailsProps = {...props, isTrackingBeta: props.trackingV2};

  // current theme: the details only ever scroll vertically - a focused
  // control a few pixels past the edge (e.g. the Tags tab bar's In Range
  // tab) made the browser scroll this hidden-x container sideways, shifting
  // every section left. Any sideways scroll is put straight back ...
  const onScroll = useCallback(function(event) {
    const element = event.currentTarget;
    if (isCurrentTheme() && element.scrollLeft) {
      element.scrollLeft = 0;
    }
  }, []);

  return (
    <div>
      <div className={classes.assetDetailOverviewWrapper}
        ref={containerRef}
        onScroll={onScroll}
        onTouchStart={onTouchHandler}
        onTouchMove={onTouchHandler}
        onTouchEnd={onTouchHandler}>
        <AssetDetailsAccordion {...assetDetailsProps} />
        {hasIOnVision && !isScDevice && (
          <VisionEventsAccordion asset={asset?.getDevice()} />
        )}
        {assetHasSmarterTrailer && (
          <SmarterTrailerAccordion smarterTrailer={selectedAssetTrailer}
            onViewTireSensorReport={hasAnalyticsPageAccess() ? onViewTireSensorReport : null}
            loading={smartTrailerLoading || sensorUpdateLoading} />
        )}
        {!isNil(attachedPeripheralType) && isSpreaderSelected && (
          <SpreaderMessageAccordion {...spreaders} isLoading={spreaderIsLoading} />
        )}
        {isGenericSensorSelected && (
          <GenericSensorAccordion data={genericSensorData}
            loading={genericSensorLoading} />
        )}
        {diagnosticDataExists && (
          <AssetDetailsDiagnostics asset={asset} />
        )}
        <TagsAccordion asset={asset} onSelectAssociatedAsset={props.onSelectAssociatedAsset} />
        {isScDevice && (
          <SCDeviceSensorGraphs />
        )}
        <AdditionalInfoAccordion asset={asset} isScDevice={isScDevice} />
      </div>
    </div>
  );
};

export default memo(AssetDetailOverviewPanel);
